import React from "react";

import { Box, CircularProgress, Typography, TablePagination, Grid, useMediaQuery, useTheme } from "@mui/material";

interface CardGridProps<T> {
  items: T[];
  renderCard: (item: T, index: number) => React.ReactNode;
  loading?: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  emptyMessage?: string;
  getItemKey?: (item: T, index: number) => string | number;
}

export function CardGrid<T>({
  items,
  renderCard,
  loading = false,
  totalCount,
  page,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  onPageSizeChange,
  emptyMessage = "No items found.",
  getItemKey,
}: CardGridProps<T>): React.ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPageSizeChange(parseInt(event.target.value, 10));
    onPageChange(0);
  };

  // Custom label to handle empty state properly - show "1-X of X" instead of "0-0 of 0"
  const labelDisplayedRows = ({ from, to, count }: { from: number; to: number; count: number }) => {
    if (count === 0) {
      return "0 of 0";
    }
    return `${from}–${to} of ${count}`;
  };

  // Use items.length as fallback if totalCount is 0 but items exist
  const effectiveTotalCount = totalCount > 0 ? totalCount : items.length;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
          width: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const isEmpty = !items || items.length === 0;

  return (
    <Box sx={{ width: "100%" }}>
      {isEmpty ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 150,
            width: "100%",
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {items.map((item, index) => (
            <Grid key={getItemKey ? getItemKey(item, index) : index} size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
              {renderCard(item, index)}
            </Grid>
          ))}
        </Grid>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: isMobile ? "center" : "flex-end",
          mt: 2,
          pt: 2,
          gap: 1,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <TablePagination
          component="div"
          count={effectiveTotalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={pageSizeOptions}
          labelRowsPerPage={isMobile ? "" : "Cards per page:"}
          labelDisplayedRows={labelDisplayedRows}
          sx={{
            ".MuiTablePagination-toolbar": {
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 0.5,
              px: isMobile ? 0 : 2,
              minHeight: isMobile ? "auto" : undefined,
            },
            ".MuiTablePagination-selectLabel": {
              display: isMobile ? "none" : "block",
              m: 0,
            },
            ".MuiTablePagination-select": {
              mr: isMobile ? 1 : 2,
            },
            ".MuiTablePagination-displayedRows": {
              m: 0,
              fontSize: isMobile ? "0.875rem" : undefined,
            },
            ".MuiTablePagination-actions": {
              ml: isMobile ? 0 : 2,
            },
          }}
        />
      </Box>
    </Box>
  );
}
